import React, { useEffect, useRef, useState } from 'react';
import { resolveApiBase } from '@/utils/apiBase';
import { 
  Check, 
  ChevronRight, 
  ChevronLeft, 
  Building2, 
  User, 
  Share2, 
  AlertTriangle,
  Plus,
  Trash2,
  Loader2
} from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';

const CORE_API = resolveApiBase(import.meta.env.VITE_CORE_API, '/core', 'http://localhost:3001');
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';
const SUPPORT_WHATSAPP = (import.meta.env.VITE_SUPPORT_WHATSAPP || import.meta.env.VITE_MANAGER_DEFAULT_WHATSAPP || '').replace(/\D/g, '');
const formatWhatsapp = (value: string) => {
  if (!value) return '';
  let digits = value.replace(/\D/g, '');
  if (digits.startsWith('55') && digits.length > 11) {
    digits = digits.slice(2);
  }
  if (digits.length > 11) {
    digits = digits.slice(-11);
  }
  if (digits.length === 11) {
    return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  }
  if (digits.length === 10) {
    return digits.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }
  return digits;
};

interface WizardProps {
  darkMode: boolean;
  onBack: () => void;
  productId?: number | null;
  successOverride?: boolean;
}

export const PublicRegistrationWizard: React.FC<WizardProps> = ({ darkMode, onBack, productId = null, successOverride = false }) => {
  const [step, setStep] = useState(1);
  const [finished, setFinished] = useState(!!successOverride);
  const [addRepresentative, setAddRepresentative] = useState(false);

  // Form States (Simplified for UI demo)
  const [formData, setFormData] = useState({
    // Step 1
    fantasyName: '',
    corporateName: '',
    cnpj: '',
    creci: '',
    address: '',
    neighborhood: '',
    zip: '',
    email: '',
    website: '',
    // Step 2
    ownerName: '',
    ownerEmail: '',
    ownerWhatsapp: '',
    repName: '',
    repEmail: '',
    repWhatsapp: '',
    // Step 3
    instagram: '',
    facebook: '',
    linkedin: ''
  });
  const [stepError, setStepError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
  const [socialErrors, setSocialErrors] = useState<{ instagram?: string; facebook?: string; linkedin?: string }>({});
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [registrationEnabled, setRegistrationEnabled] = useState<boolean | null>(null);
  const [tenantWhatsapp, setTenantWhatsapp] = useState('');
  const [tenantWhatsappMasked, setTenantWhatsappMasked] = useState('');

  // --- MASK FUNCTIONS ---
  
  const maskCNPJ = (value: string) => {
    return value
      .replace(/\D/g, '') // Remove tudo o que não é dígito
      .replace(/^(\d{2})(\d)/, '$1.$2') // Coloca ponto entre o segundo e o terceiro dígitos
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3') // Coloca ponto entre o quinto e o sexto dígitos
      .replace(/\.(\d{3})(\d)/, '.$1/$2') // Coloca uma barra entre o oitavo e o nono dígitos
      .replace(/(\d{4})(\d)/, '$1-$2') // Coloca um hífen depois do bloco de quatro dígitos
      .substring(0, 18); // Limita o tamanho
  };

  const maskPhone = (value: string) => {
    let v = value.replace(/\D/g, '');
    v = v.substring(0, 11); // Limita a 11 dígitos

    // Formata (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
    if (v.length > 10) {
      return v.replace(/^(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (v.length > 5) {
      return v.replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    } else if (v.length > 2) {
      return v.replace(/^(\d{2})(\d{0,5})/, '($1) $2');
    } else {
      return v.replace(/^(\d*)/, '($1');
    }
  };

  const buildSocialUrl = (value: string, provider: 'instagram' | 'facebook' | 'linkedin') => {
    const trimmed = (value || '').trim();
    if (!trimmed) return '';

    const noAt = provider === 'instagram' && trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
    const lower = noAt.toLowerCase();
    const hasScheme = /^https?:\/\//i.test(noAt);
    const hasProviderDomain =
      lower.includes('instagram.com') || lower.includes('facebook.com') || lower.includes('linkedin.com');
    const looksLikeHandle = !hasScheme && !hasProviderDomain && !noAt.includes('/');

    if (looksLikeHandle) {
      if (provider === 'linkedin') return `https://www.linkedin.com/company/${noAt}`;
      if (provider === 'facebook') return `https://www.facebook.com/${noAt}`;
      return `https://www.instagram.com/${noAt}`;
    }

    const raw = hasScheme ? noAt : `https://${noAt}`;
    try {
      const parsed = new URL(raw);
      const hostname = parsed.hostname.toLowerCase();
      const validHost =
        (provider === 'instagram' && hostname.endsWith('instagram.com')) ||
        (provider === 'facebook' && hostname.endsWith('facebook.com')) ||
        (provider === 'linkedin' && hostname.endsWith('linkedin.com'));
      if (!validHost) return '';
      return `${parsed.protocol}//${parsed.hostname}${parsed.pathname}`.replace(/\/+$/, '');
    } catch {
      return '';
    }
  };

  const validateSocial = (field: 'instagram' | 'facebook' | 'linkedin', value: string) => {
    if (!value.trim()) return { error: '' };
    const normalized = buildSocialUrl(value, field);
    if (!normalized) {
      const label = field === 'instagram' ? 'instagram.com' : field === 'facebook' ? 'facebook.com' : 'linkedin.com';
      return { error: `Link invalido. Use um link do ${label} ou apenas o usuario.` };
    }
    return { error: '' };
  };

  const handleChange = (field: string, value: string) => {
    let finalValue = value;

    // Apply Masks
    if (field === 'cnpj') {
      finalValue = maskCNPJ(value);
    } else if (['ownerWhatsapp', 'repWhatsapp'].includes(field)) {
      finalValue = maskPhone(value);
    } else if (['email', 'ownerEmail', 'repEmail', 'website'].includes(field)) {
      finalValue = value.toLowerCase(); // Force lowercase for emails/web
    }

    setFormData(prev => ({ ...prev, [field]: finalValue }));
    setStepError(null);
    setSubmitError(null);
    setSubmitSuccess(null);
    if (field === 'instagram' || field === 'facebook' || field === 'linkedin') {
      setSocialErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const stepOneValid =
    formData.fantasyName.trim() &&
    formData.corporateName.trim() &&
    formData.cnpj.trim() &&
    formData.creci.trim() &&
    formData.address.trim() &&
    formData.neighborhood.trim() &&
    formData.zip.trim() &&
    formData.email.trim() &&
    formData.website.trim();

  const stepTwoValid = (() => {
    const ownerOk =
      formData.ownerName.trim() &&
      formData.ownerEmail.trim() &&
      formData.ownerWhatsapp.trim();
    if (!addRepresentative) return ownerOk;
    return ownerOk &&
      formData.repName.trim() &&
      formData.repEmail.trim() &&
      formData.repWhatsapp.trim();
  })();

  const stepThreeValid = true; // redes sociais opcionais

  const isStepValid = (currentStep: number) => {
    if (currentStep === 1) return !!stepOneValid;
    if (currentStep === 2) return !!stepTwoValid;
    if (currentStep === 3) return !!stepThreeValid;
    return true;
  };

  const isRegistrationEnabled = (id?: number | null) => {
    if (!id) return false;
    if (registrationEnabled === null) return true;
    return registrationEnabled;
  };

  const supportWhatsapp = tenantWhatsapp || SUPPORT_WHATSAPP;
  const supportWhatsappDisplay = tenantWhatsappMasked || formatWhatsapp(supportWhatsapp);

  const nextStep = async () => {
    if (!isStepValid(step)) {
      setStepError('Preencha todos os campos obrigatórios antes de continuar.');
      return;
    }
    setStep(prev => Math.min(prev + 1, 3));
    setStepError(null);
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
    setStepError(null);
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-10">
      {[1, 2, 3].map((num) => (
        <div key={num} className="flex items-center">
          <div className={`
            w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all
            ${step === num 
              ? 'bg-[#294b29] text-white scale-110 shadow-lg shadow-green-900/30' 
              : step > num 
                ? 'bg-green-800 text-white' 
                : (darkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-200 text-gray-400')
            }
          `}>
            {step > num ? <Check size={20} /> : num}
          </div>
          {num < 3 && (
            <div className={`
              w-12 h-1 mx-2 rounded-full
              ${step > num 
                ? 'bg-[#294b29]' 
                : (darkMode ? 'bg-gray-800' : 'bg-gray-200')
              }
            `} />
          )}
        </div>
      ))}
    </div>
  );

  const handleSubmit = async () => {
    setStepError(null);
    setSubmitSuccess(null);
    setRecaptchaError(null);

    if (registrationEnabled === false) {
      setStepError('Credenciamento fechado. Procure a coordenacao.');
      return;
    }

    if (!isStepValid(3)) {
      setStepError('Preencha todos os campos obrigatórios antes de finalizar.');
      return;
    }
    if (!RECAPTCHA_SITE_KEY) {
      setRecaptchaError('Configuração do reCAPTCHA ausente. Avise o suporte.');
      return;
    }
    if (!recaptchaToken) {
      setRecaptchaError('Confirme que você não é um robô.');
      return;
    }
    if (!productId) {
      setSubmitError('Produto não informado no link de cadastro.');
      return;
    }

    const instagramError = validateSocial('instagram', formData.instagram).error;
    const facebookError = validateSocial('facebook', formData.facebook).error;
    const linkedinError = validateSocial('linkedin', formData.linkedin).error;
    if (instagramError || facebookError || linkedinError) {
      setSocialErrors({
        instagram: instagramError || undefined,
        facebook: facebookError || undefined,
        linkedin: linkedinError || undefined
      });
      setStepError('Ajuste os links das redes sociais antes de finalizar.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const instagramUrl = buildSocialUrl(formData.instagram, 'instagram');
      const facebookUrl = buildSocialUrl(formData.facebook, 'facebook');
      const linkedinUrl = buildSocialUrl(formData.linkedin, 'linkedin');
      const payload = {
        produtoId: productId,
        fantasyName: formData.fantasyName.trim(),
        corporateName: formData.corporateName.trim(),
        cnpj: formData.cnpj.trim(),
        creci: formData.creci.trim(),
        address: formData.address.trim(),
        neighborhood: formData.neighborhood.trim(),
        zip: formData.zip.trim(),
        email: formData.email.trim(),
        website: formData.website.trim(),
        ownerName: formData.ownerName.trim(),
        ownerEmail: formData.ownerEmail.trim(),
        ownerWhatsapp: formData.ownerWhatsapp.trim(),
        repName: addRepresentative ? formData.repName.trim() : null,
        repEmail: addRepresentative ? formData.repEmail.trim() : null,
        repWhatsapp: addRepresentative ? formData.repWhatsapp.trim() : null,
        instagram: instagramUrl,
        facebook: facebookUrl,
        linkedin: linkedinUrl,
        recaptchaToken
      };

      const response = await fetch(`${CORE_API}/public/imobiliarias`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(json.error || 'Não foi possível enviar seu cadastro.');
      }

      setSubmitSuccess('Cadastro enviado com sucesso! Em breve entraremos em contato.');
      setFinished(true);
      try {
        const tenantSlug = localStorage.getItem('va_tenant_name') || localStorage.getItem('va_tenant_id') || '';
        if (tenantSlug) {
          window.history.replaceState({}, '', `/${tenantSlug}/cadastro/${productId}/sucesso`);
        }
      } catch {}
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao enviar cadastro. Tente novamente.';
      setSubmitError(msg);
      try {
        recaptchaRef.current?.reset?.();
        setRecaptchaToken(null);
      } catch {}
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
    setRecaptchaError(null);
    setSubmitError(null);
  };

  useEffect(() => {
    const fetchRegistrationStatus = async () => {
      if (!productId) return;
      try {
        const response = await fetch(`${CORE_API}/public/produtos/${productId}/credenciamento-info`);
        if (!response.ok) throw new Error('Falha ao buscar credenciamento');
        const data = await response.json();
        const enabled = data?.enabled;
        const phone = String(data?.whatsappNumber || '').replace(/\D/g, '');
        const masked = String(data?.whatsappNumberMasked || '').trim();
        setRegistrationEnabled(enabled === undefined ? true : !!enabled);
        setTenantWhatsapp(phone || '');
        setTenantWhatsappMasked(masked || '');
      } catch {
        setRegistrationEnabled(true);
      }
    };
    fetchRegistrationStatus();
  }, [productId]);

  return (
    <div className={`min-h-screen flex flex-col items-center py-10 px-4 ${darkMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-800'}`}>
      
      {/* Header / Logo Area */}
      <div className="mb-8 text-center">
         <div className="flex items-center justify-center gap-2 mb-2">
            <svg viewBox="0 0 200 60" className="h-12 w-auto" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M30 10C18.954 10 10 18.954 10 30C10 41.046 18.954 50 30 50C35.5 50 40.5 47.8 44.1 44.1L50 50L55 45L49.1 39.1C52.8 35.5 55 30.5 55 30C55 18.954 46.046 10 35 10H30Z" fill="#294b29" stroke="#294b29" strokeWidth="5"/>
              <circle cx="30" cy="30" r="8" fill="white"/>
              <circle cx="38" cy="22" r="6" fill="#FF0000"/>
              <text x="65" y="40" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize="32" fill={darkMode ? "#ffffff" : "#294b29"}>VISÃO</text>
           </svg>
         </div>
         <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Cadastro de Parceiro Imobiliário</p>
      </div>

      <div className={`
        w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden
        ${darkMode ? 'bg-vision-darkGreen border border-vision-border' : 'bg-white'}
      `}>
        <div className="p-8">
          {isRegistrationEnabled(productId) && renderStepIndicator()}

          {(stepError || submitError || submitSuccess || recaptchaError) && (
            <div className="mb-6 space-y-2">
              {stepError && (
                <div className={`px-4 py-3 rounded-lg text-sm ${darkMode ? 'bg-red-900/30 text-red-200 border border-red-800' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {stepError}
                </div>
              )}
              {submitError && (
                <div className={`px-4 py-3 rounded-lg text-sm ${darkMode ? 'bg-red-900/30 text-red-200 border border-red-800' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {submitError}
                </div>
              )}
              {recaptchaError && (
                <div className={`px-4 py-3 rounded-lg text-sm ${darkMode ? 'bg-red-900/30 text-red-200 border border-red-800' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {recaptchaError}
                </div>
              )}
              {submitSuccess && (
                <div className={`px-4 py-3 rounded-lg text-sm ${darkMode ? 'bg-green-900/30 text-green-200 border border-green-800' : 'bg-green-50 text-green-800 border border-green-200'}`}>
                  {submitSuccess}
                </div>
              )}
            </div>
          )}

          {/* Steps Content */}
          <div className="min-h-[400px]">
          {(!productId || Number.isNaN(productId)) ? (
            <div className="text-center space-y-3 py-10">
              <h2 className="text-2xl font-bold">Link inválido</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Produto não informado, link incompleto ou cadastro desativado para este empreendimento.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Fale com o suporte {supportWhatsappDisplay ? `pelo WhatsApp ${supportWhatsappDisplay}` : 'no WhatsApp'} para continuar o credenciamento.
              </p>
              <button
                onClick={onBack}
                className="px-6 py-3 rounded-lg font-semibold bg-[#294b29] text-white hover:bg-[#1a301a] transition-colors"
              >
                Voltar
              </button>
            </div>
          ) : !isRegistrationEnabled(productId) ? (
            <div className="text-center space-y-3 py-10">
              <h2 className="text-2xl font-bold">Credenciamento fechado</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Procure a coordenacao para liberar o cadastro.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Fale com o suporte {supportWhatsappDisplay ? `pelo WhatsApp ${supportWhatsappDisplay}` : 'no WhatsApp'}.
              </p>
              <a
                href={supportWhatsapp ? `https://wa.me/${supportWhatsapp}?text=Ol%C3%A1%2C%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es%20p%2F%20credenciamento.` : '#'}
                target="_blank"
                rel="noreferrer"
                className={`inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold text-white transition-colors ${
                  supportWhatsapp ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed'
                }`}
                aria-disabled={!supportWhatsapp}
                onClick={(event) => {
                  if (!supportWhatsapp) event.preventDefault();
                }}
              >
                Chamar no WhatsApp
              </a>
            </div>
          ) : finished ? (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 text-center space-y-4 py-10">
              <div className="w-16 h-16 rounded-full bg-green-100 text-green-700 flex items-center justify-center mx-auto">
                <Check size={28} />
              </div>
              <h2 className="text-2xl font-bold">Cadastro enviado!</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Parabéns, recebemos seu cadastro. Em breve nossa equipe entrará em contato.
              </p>
              <p className="text-xs text-gray-400">
                Você já pode fechar esta página.
              </p>
            </div>
          ) : (
            <>
            {/* STEP 1: IMOBILIÁRIA */}
            {step === 1 && (
              <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                <h2 className="text-2xl font-bold mb-1 flex items-center gap-2">
                  <Building2 className="text-[#294b29]" />
                  Imobiliária
                </h2>
                <p className={`text-sm mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Dados da pessoa jurídica</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold ml-1">Nome Fantasia</label>
                    <input 
                      className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900' : 'bg-gray-50 border-gray-200 focus:ring-[#294b29]/20'}`}
                      placeholder="Ex: Imobiliária Alpha"
                      value={formData.fantasyName}
                      onChange={(e) => handleChange('fantasyName', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold ml-1">Razão Social</label>
                    <input 
                      className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900' : 'bg-gray-50 border-gray-200 focus:ring-[#294b29]/20'}`}
                      placeholder="Ex: Alpha Negócios Imobiliários Ltda"
                      value={formData.corporateName}
                      onChange={(e) => handleChange('corporateName', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold ml-1">CNPJ</label>
                    <input 
                      className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900' : 'bg-gray-50 border-gray-200 focus:ring-[#294b29]/20'}`}
                      placeholder="00.000.000/0000-00"
                      value={formData.cnpj}
                      onChange={(e) => handleChange('cnpj', e.target.value)}
                      maxLength={18}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold ml-1">CRECI PJ</label>
                    <input 
                      className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900' : 'bg-gray-50 border-gray-200 focus:ring-[#294b29]/20'}`}
                      placeholder="J-12345"
                      value={formData.creci}
                      onChange={(e) => handleChange('creci', e.target.value)}
                    />
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <label className="text-sm font-semibold ml-1">Endereço Completo</label>
                    <input 
                      className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900' : 'bg-gray-50 border-gray-200 focus:ring-[#294b29]/20'}`}
                      placeholder="Rua, Número, Complemento"
                      value={formData.address}
                      onChange={(e) => handleChange('address', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold ml-1">Bairro</label>
                    <input 
                      className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900' : 'bg-gray-50 border-gray-200 focus:ring-[#294b29]/20'}`}
                      value={formData.neighborhood}
                      onChange={(e) => handleChange('neighborhood', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold ml-1">CEP</label>
                    <input 
                      className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900' : 'bg-gray-50 border-gray-200 focus:ring-[#294b29]/20'}`}
                      value={formData.zip}
                      onChange={(e) => handleChange('zip', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold ml-1">Email Corporativo</label>
                    <input 
                      type="email"
                      className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900' : 'bg-gray-50 border-gray-200 focus:ring-[#294b29]/20'}`}
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="contato@empresa.com.br"
                    />
                  </div>
                   <div className="space-y-2">
                    <label className="text-sm font-semibold ml-1">Website</label>
                    <input 
                      type="url"
                      className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900' : 'bg-gray-50 border-gray-200 focus:ring-[#294b29]/20'}`}
                      placeholder="www.suaimobiliaria.com.br"
                      value={formData.website}
                      onChange={(e) => handleChange('website', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 2: SÓCIO PROPRIETÁRIO */}
            {step === 2 && (
              <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                <h2 className="text-2xl font-bold mb-1 flex items-center gap-2">
                  <User className="text-[#294b29]" />
                  Sócio Proprietário
                </h2>
                <p className={`text-sm mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Dados do responsável legal</p>

                {/* Alert Box */}
                <div className="mb-8 p-4 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg flex gap-3">
                   <AlertTriangle className="text-yellow-600 flex-shrink-0" />
                   <div>
                     <h4 className={`text-sm font-bold ${darkMode ? 'text-yellow-400' : 'text-yellow-800'}`}>Atenção ao Responsável</h4>
                     <p className={`text-xs mt-1 ${darkMode ? 'text-yellow-200/70' : 'text-yellow-800/80'}`}>
                       Este cadastro deve conter obrigatoriamente os dados da pessoa que <strong className="underline">assina legalmente</strong> pela empresa.
                     </p>
                   </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2 space-y-2">
                    <label className="text-sm font-semibold ml-1">Nome Completo (Sócio)</label>
                    <input 
                      className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900' : 'bg-gray-50 border-gray-200 focus:ring-[#294b29]/20'}`}
                      value={formData.ownerName}
                      onChange={(e) => handleChange('ownerName', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold ml-1">Email Pessoal</label>
                    <input 
                      type="email"
                      className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900' : 'bg-gray-50 border-gray-200 focus:ring-[#294b29]/20'}`}
                      value={formData.ownerEmail}
                      onChange={(e) => handleChange('ownerEmail', e.target.value)}
                      placeholder="email@pessoal.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold ml-1">WhatsApp</label>
                    <input 
                      className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900' : 'bg-gray-50 border-gray-200 focus:ring-[#294b29]/20'}`}
                      placeholder="(DD) 90000-0000"
                      value={formData.ownerWhatsapp}
                      onChange={(e) => handleChange('ownerWhatsapp', e.target.value)}
                      maxLength={15}
                    />
                  </div>
                </div>

                {/* Representative Section */}
                <div className="mt-8 pt-8 border-t border-dashed border-gray-300 dark:border-gray-700">
                  {!addRepresentative ? (
                    <button 
                      onClick={() => setAddRepresentative(true)}
                      className="flex items-center gap-2 text-[#294b29] font-bold hover:underline"
                    >
                      <Plus size={20} />
                      Adicionar Representante Comercial
                    </button>
                  ) : (
                    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold">Representante Comercial</h3>
                        <button onClick={() => setAddRepresentative(false)} className="text-red-500 hover:bg-red-50 p-2 rounded-full"><Trash2 size={18}/></button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 dark:bg-black/20 p-6 rounded-xl">
                        <div className="md:col-span-2 space-y-2">
                          <label className="text-sm font-semibold ml-1">Nome do Representante</label>
                          <input 
                            className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900' : 'bg-white border-gray-200 focus:ring-[#294b29]/20'}`}
                            value={formData.repName}
                            onChange={(e) => handleChange('repName', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold ml-1">Email</label>
                          <input 
                            type="email"
                            className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900' : 'bg-white border-gray-200 focus:ring-[#294b29]/20'}`}
                            value={formData.repEmail}
                            onChange={(e) => handleChange('repEmail', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold ml-1">WhatsApp</label>
                          <input 
                            className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900' : 'bg-white border-gray-200 focus:ring-[#294b29]/20'}`}
                            value={formData.repWhatsapp}
                            onChange={(e) => handleChange('repWhatsapp', e.target.value)}
                            placeholder="(DD) 90000-0000"
                            maxLength={15}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* STEP 3: REDES SOCIAIS */}
            {step === 3 && (
              <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                <h2 className="text-2xl font-bold mb-1 flex items-center gap-2">
                  <Share2 className="text-[#294b29]" />
                  Redes Sociais
                </h2>
                <p className={`text-sm mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Onde podemos encontrar sua empresa? (opcional)</p>
                
                <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold ml-1">Instagram</label>
                    <div className="relative">
                       <span className="absolute left-4 top-3.5 text-gray-400 font-medium">@</span>
                       <input 
                        className={`w-full pl-8 pr-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900' : 'bg-gray-50 border-gray-200 focus:ring-[#294b29]/20'}`}
                        placeholder="imobiliaria.alpha"
                        value={formData.instagram}
                        onChange={(e) => handleChange('instagram', e.target.value)}
                        onBlur={(e) => {
                          const error = validateSocial('instagram', e.target.value).error;
                          setSocialErrors(prev => ({ ...prev, instagram: error || undefined }));
                        }}
                      />
                    </div>
                    {socialErrors.instagram && (
                      <p className="text-xs text-red-500">{socialErrors.instagram}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold ml-1">Facebook</label>
                    <input 
                      className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900' : 'bg-gray-50 border-gray-200 focus:ring-[#294b29]/20'}`}
                      placeholder="facebook.com/pagina"
                      value={formData.facebook}
                      onChange={(e) => handleChange('facebook', e.target.value)}
                      onBlur={(e) => {
                        const error = validateSocial('facebook', e.target.value).error;
                        setSocialErrors(prev => ({ ...prev, facebook: error || undefined }));
                      }}
                    />
                    {socialErrors.facebook && (
                      <p className="text-xs text-red-500">{socialErrors.facebook}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold ml-1">LinkedIn</label>
                    <input 
                      className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900' : 'bg-gray-50 border-gray-200 focus:ring-[#294b29]/20'}`}
                      placeholder="linkedin.com/company/..."
                      value={formData.linkedin}
                      onChange={(e) => handleChange('linkedin', e.target.value)}
                      onBlur={(e) => {
                        const error = validateSocial('linkedin', e.target.value).error;
                        setSocialErrors(prev => ({ ...prev, linkedin: error || undefined }));
                      }}
                    />
                    {socialErrors.linkedin && (
                      <p className="text-xs text-red-500">{socialErrors.linkedin}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            </>
            )}
          </div>

          {/* Footer Navigation */}
          {!finished && isRegistrationEnabled(productId) && (
            <div className="mt-10 flex flex-col gap-4 pt-6 border-t border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between gap-4">
                {step > 1 ? (
                  <button 
                    onClick={prevStep}
                    disabled={isSubmitting}
                    className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    <ChevronLeft size={20} /> Voltar
                  </button>
                ) : (
                   <button 
                    onClick={onBack}
                    disabled={isSubmitting}
                    className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors text-gray-400 hover:text-gray-600 ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    Cancelar
                  </button>
                )}

                {step < 3 ? (
                   <button 
                     onClick={nextStep}
                     className="flex items-center gap-2 px-8 py-3 rounded-lg font-bold bg-[#294b29] text-white hover:bg-[#1a301a] shadow-lg shadow-green-900/20 active:scale-95 transition-all"
                   >
                     Próximo <ChevronRight size={20} />
                   </button>
                 ) : (
                   <button 
                     onClick={handleSubmit}
                     disabled={isSubmitting}
                     className={`flex items-center gap-2 px-8 py-3 rounded-lg font-bold bg-[#294b29] text-white hover:bg-[#1a301a] shadow-lg shadow-green-900/20 active:scale-95 transition-all ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
                   >
                     {isSubmitting ? <Loader2 className="animate-spin" size={20} /> : <Check size={20} />} Finalizar Cadastro
                   </button>
                 )}
              </div>

              <div className="flex items-center justify-center">
                {RECAPTCHA_SITE_KEY ? (
                  <ReCAPTCHA
                    key="wizard-recaptcha"
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={handleRecaptchaChange}
                    onErrored={() => {
                      setRecaptchaError('Falha ao carregar reCAPTCHA. Tente novamente.');
                      try {
                        recaptchaRef.current?.reset?.();
                        setRecaptchaToken(null);
                      } catch {}
                    }}
                    theme={darkMode ? 'dark' : 'light'}
                  />
                ) : (
                  <div className={`px-4 py-3 rounded-lg text-sm ${darkMode ? 'bg-yellow-900/30 text-yellow-200 border border-yellow-800' : 'bg-yellow-50 text-yellow-800 border border-yellow-200'}`}>
                    Configuração do reCAPTCHA ausente. Contate o suporte.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="mt-8 text-center text-xs text-gray-500">
        &copy; 2024 Visão Alpha. Todos os direitos reservados.
      </div>

    </div>
  );

  useEffect(() => {
    if (step === 3 && !finished) {
      setRecaptchaToken(null);
      setRecaptchaError(null);

    if (!isRegistrationEnabled(productId || null)) {
      setStepError('Credenciamento fechado. Procure a coordenacao.');
      return;
    }

      try {
        recaptchaRef.current?.reset?.();
      } catch {}
    }
  }, [step, finished]);
};








